import { lazy, Suspense, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { SectionHeading, Reveal } from "./Reveal";
import { FallbackNetwork } from "./FallbackNetwork";
import { useCanRender3D } from "../hooks/useCanRender3D";
import { NETWORK_NODES } from "../data/content";

const NetworkScene = lazy(() => import("./three/NetworkScene"));

export const TechNetwork = () => {
  const can3d = useCanRender3D();
  const [activeId, setActiveId] = useState(null);
  const active = NETWORK_NODES.find((n) => n.id === activeId);

  return (
    <section id="technology" className="relative border-y hairline bg-card/30 py-24 sm:py-32" data-testid="technology-section">
      <div className="mx-auto max-w-7xl px-4 sm:px-8 lg:px-16">
        <SectionHeading
          index="04"
          eyebrow="Technology"
          title="One engineering partner. Multiple technology layers."
          sub="Explore the layers of a modern software system — hover any node to see how Farmint engineers it."
        />
        <div className="mt-14 grid grid-cols-1 items-center gap-10 lg:grid-cols-5">
          <Reveal className="lg:col-span-3">
            <div className="relative h-[420px] overflow-hidden rounded-2xl border hairline bg-background/50 sm:h-[520px]">
              <div className="absolute inset-0 grid-texture opacity-60" aria-hidden="true" />
              {can3d ? (
                <Suspense fallback={null}>
                  <NetworkScene activeId={activeId} onHover={setActiveId} />
                </Suspense>
              ) : (
                <FallbackNetwork
                  labels={NETWORK_NODES.filter((n) => n.id !== "platform").map((n) => n.name)}
                  center="Platform"
                  className="absolute inset-0 m-auto h-full max-h-[440px] w-full text-foreground"
                />
              )}
            </div>
          </Reveal>
          <Reveal delay={0.15} className="lg:col-span-2">
            <div className="min-h-[150px] rounded-2xl border hairline bg-card/70 p-7" data-testid="network-info-panel">
              <AnimatePresence mode="wait">
                <motion.div
                  key={active ? active.id : "default"}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{ duration: 0.25 }}
                >
                  <p className="font-mono text-xs uppercase tracking-[0.2em] text-primary font-semibold">
                    {active ? active.name : "Architecture view"}
                  </p>
                  <p className="mt-3 text-sm sm:text-base leading-relaxed text-muted-foreground">
                    {active
                      ? active.desc
                      : "Every product is a system of layers — interfaces, platform core, APIs, data, cloud and delivery pipelines. Farmint designs and builds across all of them, so nothing falls between vendors."}
                  </p>
                </motion.div>
              </AnimatePresence>
            </div>
            <div className="mt-5 flex flex-wrap gap-2">
              {NETWORK_NODES.map((n) => (
                <button
                  key={n.id}
                  data-testid={`network-chip-${n.id}`}
                  onMouseEnter={() => setActiveId(n.id)}
                  onMouseLeave={() => setActiveId(null)}
                  onFocus={() => setActiveId(n.id)}
                  onBlur={() => setActiveId(null)}
                  className={`rounded-full border px-3.5 py-1.5 font-mono text-[11px] uppercase tracking-[0.14em] transition-colors duration-200 ${
                    activeId === n.id
                      ? "border-primary/60 bg-primary/10 text-primary"
                      : "hairline text-muted-foreground hover:text-foreground"
                  }`}
                >
                  {n.name}
                </button>
              ))}
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
};
